import {
    FunctionComponent,
    useRef,
    useState,
    useEffect,
    useCallback,
} from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
    VoucherTemplateElement,
    VoucherTemplateElementType,
    ELEMENT_TYPES,
} from '@starjack/entities/voucherTemplate';
import { EditorElement } from './EditorElement';

// A4 PDF dimensions in points (1/72 inch), matching iText coordinate system
const PDF_WIDTH = 595;
const PDF_HEIGHT = 842;
const ASPECT_RATIO = PDF_HEIGHT / PDF_WIDTH;

type EditorViewportProps = {
    elements: Record<VoucherTemplateElementType, VoucherTemplateElement>;
    selectedType: VoucherTemplateElementType | null;
    backgroundUrl: string | null;
    backgroundLoading?: boolean;
    defaultText?: string;
    onSelect: (type: VoucherTemplateElementType | null) => void;
    onMove: (type: VoucherTemplateElementType, x: number, y: number) => void;
    onResize: (
        type: VoucherTemplateElementType,
        w: number,
        h: number,
        x: number,
        y: number
    ) => void;
};

export const EditorViewport: FunctionComponent<EditorViewportProps> = ({
    elements,
    selectedType,
    backgroundUrl,
    backgroundLoading,
    defaultText,
    onSelect,
    onMove,
    onResize,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scaleFactor, setScaleFactor] = useState(1);
    const [containerWidth, setContainerWidth] = useState(PDF_WIDTH);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!selectedType) return;

            // Skip if typing in an input
            if (
                e.target instanceof HTMLInputElement ||
                e.target instanceof HTMLTextAreaElement
            ) {
                return;
            }

            // Delete/Backspace deselects
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                onSelect(null);
                return;
            }

            // Escape deselects
            if (e.key === 'Escape') {
                onSelect(null);
                return;
            }

            // Arrow keys nudge selected element
            const step = e.shiftKey ? 0.02 : 0.005;
            const el = elements[selectedType];
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                onMove(selectedType, Math.max(0, el.x - step), el.y);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                onMove(selectedType, Math.min(1 - el.w, el.x + step), el.y);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                onMove(selectedType, el.x, Math.max(0, el.y - step));
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                onMove(selectedType, el.x, Math.min(1 - el.h, el.y + step));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedType, elements, onSelect, onMove]);

    // Calculate scale factor based on available container width
    const updateScale = useCallback(() => {
        if (containerRef.current) {
            const availableWidth = containerRef.current.offsetWidth;
            const scale = availableWidth / PDF_WIDTH;
            setScaleFactor(scale);
            setContainerWidth(availableWidth);
        }
    }, []);

    useEffect(() => {
        updateScale();
        const observer = new ResizeObserver(updateScale);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, [updateScale]);

    const handleCanvasClick = useCallback(
        (e: React.MouseEvent) => {
            // Deselect when clicking on canvas background (not on an element)
            if (e.target === e.currentTarget) {
                onSelect(null);
            }
        },
        [onSelect]
    );

    const handleResize = useCallback(
        (
            type: VoucherTemplateElementType,
            w: number,
            h: number,
            x: number,
            y: number
        ) => {
            // Single state update: resize includes position to avoid race condition
            onResize(type, w, h, x, y);
        },
        [onResize]
    );

    const containerHeight = containerWidth * ASPECT_RATIO;

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                maxWidth: PDF_WIDTH,
                mx: 'auto',
                position: 'relative',
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            {/* Scaled viewport */}
            <Box
                onClick={handleCanvasClick}
                sx={{
                    width: containerWidth,
                    height: containerHeight,
                    position: 'relative',
                    backgroundImage: backgroundUrl
                        ? `url(${backgroundUrl})`
                        : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Loading / empty state */}
                {!backgroundUrl && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            pointerEvents: 'none',
                            zIndex: 0,
                        }}
                    >
                        {backgroundLoading ? (
                            <CircularProgress size={40} />
                        ) : (
                            <>
                                <Typography
                                    variant='h6'
                                    color='text.secondary'
                                    gutterBottom
                                >
                                    Kein Hintergrund-PDF
                                </Typography>
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    Laden Sie eine PDF-Datei als Hintergrund
                                    hoch, um die Felder zu positionieren.
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                {/* Draggable elements */}
                {ELEMENT_TYPES.map((type) => (
                    <EditorElement
                        key={type}
                        element={elements[type]}
                        defaultText={type === 'TEXT' ? defaultText : undefined}
                        isSelected={selectedType === type}
                        canvasWidth={PDF_WIDTH}
                        canvasHeight={PDF_HEIGHT}
                        scaleFactor={scaleFactor}
                        onSelect={onSelect}
                        onMove={onMove}
                        onResize={handleResize}
                    />
                ))}
            </Box>
        </Box>
    );
};
